// frontend/src/pages/Overview.jsx
import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { TX_LABELS } from "../constants";
import { API, fetchJSON } from "../api.js";
import InlineFilters from "../components/InlineFilters.jsx";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import { getStatusLabel } from "../filterCConstants.js";
import Pagination from "../components/Pagination.jsx";

const yn = (v) => (v ? "Y" : "N");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (!Number.isFinite(dt.getTime())) return "—";
  return `${MONTHS[dt.getMonth()]}-${String(dt.getDate()).padStart(2, "0")}`;
};
const titleCase = (input) => {
  const s = (input ?? "")
    .toString()
    .replace(/[_\-]+/g, " ")
    .trim();
  if (!s) return "—";
  return s
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");
};

const parseArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim().startsWith("[")) {
    try {
      return JSON.parse(v);
    } catch {
      return [];
    }
  }
  if (typeof v === "string") return v.split(",");
  return [];
};

// ✅ NEW: Add this formatter function with the others
const managedByLabel = (v) => {
  if (!v) return "—";
  const normalized = String(v).toLowerCase();
  if (normalized === "gtrust") return "G-Trust Curaçao B.V.";
  if (normalized === "prostaff") return "ProStaff Curacao N.V.";
  return titleCase(v);
};

const statusLabel = (raw) => {
  const key = String(raw || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  const statusItem = getStatusLabel(key);
  if (statusItem !== key) return statusItem;

  const map = {
    pending_gov_approval: "Pending Gov Approval",
    active: "Active",
    rejected: "Rejected",
    liquidated: "Liquidated",
    resigned: "Resigned",
  };

  return map[key] || (raw ? String(raw).replace(/[_-]+/g, " ").toUpperCase() : "UNKNOWN");
};

const triLabel = (v) => (v === 1 || v === "1" ? "Y" : v === 0 || v === "0" ? "N" : "N/A");

const fmtOrigins = (v) => {
  const arr = parseArray(v);
  const seen = new Set();
  const out = [];
  arr.forEach((s) => {
    let t = titleCase(String(s).trim());
    const key = t.toLowerCase();
    if (!t || seen.has(key)) return;
    seen.add(key);
    out.push(t);
  });
  return out.join(", ") || "—";
};

const fmtTx = (v) => {
  let arr = parseArray(v)
    .map((s) => String(s).trim())
    .filter(Boolean);
  if (arr.length === 2) {
    const lc = arr.map((s) => s.replace(/[^a-z]/gi, "").toLowerCase());
    if (lc.includes("n") && lc.includes("a")) return "N/A";
  }

  const seen = new Set();
  const out = [];
  arr.forEach((s) => {
    let key = s.toLowerCase().replace(/[^a-z0-9]+/g, "_");
    if (key === "crypto-related") key = "crypto_related";
    if (seen.has(key)) return;
    seen.add(key);
    out.push(TX_LABELS[key] || titleCase(key));
  });
  return out.join(", ") || "—";
};

function formatMoney(n, ccy) {
  if (n == null || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  const s = num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const map = { USD: "USD", EUR: "EUR", XCG: "XCG" };
  const tag = map[ccy?.toUpperCase?.()] || (ccy || "").toUpperCase();
  return tag ? `${s} ${tag}` : s;
}

const fmtYear = (v) => (v ? String(v).slice(0, 4) : "—");

export default function Overview() {
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFromUrl = parseInt(searchParams.get("page") || "1", 10);
  const pageSizeFromUrl = parseInt(searchParams.get("pageSize") || "25", 10);
  const qFromUrl = searchParams.get("q") || "";

  const filtersFromUrl = {};
  for (const [key, value] of searchParams.entries()) {
    if (key !== "page" && key !== "pageSize" && key !== "q") {
      if (!filtersFromUrl[key]) {
        filtersFromUrl[key] = [];
      }
      filtersFromUrl[key].push(value);
    }
  }

  const [data, setData] = useState({ items: [], total: 0, page: pageFromUrl, pageSize: pageSizeFromUrl });
  const [q, setQ] = useState(qFromUrl);
  const [filters, setFilters] = useState(filtersFromUrl);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const [filterOpen, setFilterOpen] = useState(false);
  const filterAnchorRef = useRef(null);

  const normalizeFilter = (obj = {}) => {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (Array.isArray(v) && v.length) out[k] = v;
      else if (typeof v === "string" && v) out[k] = v;
    });
    return out;
  };

  const updateUrl = (page, pageSize, searchQuery, nextFilters) => {
    const params = new URLSearchParams();

    params.set("page", String(page));
    params.set("pageSize", String(pageSize));

    if (searchQuery && searchQuery.trim()) {
      params.set("q", searchQuery.trim());
    }

    Object.entries(nextFilters).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        v.forEach((x) => params.append(k, x));
      } else if (v) {
        params.append(k, v);
      }
    });

    setSearchParams(params);
  };

  async function fetchPage(page = 1, nextFilters = filters, searchQuery = q) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("pageSize", String(data.pageSize || 25));
      if (searchQuery && searchQuery.trim()) {
        params.set("q", searchQuery.trim());
      }
      Object.entries(nextFilters).forEach(([k, v]) => {
        if (Array.isArray(v)) v.forEach((x) => params.append(k, x));
        else if (v) params.append(k, v);
      });

      const json = await fetchJSON(`${API}/browse/companies?${params}`, {
        portal: true,
      });

      setData(json);

      updateUrl(page, data.pageSize || 25, searchQuery, nextFilters);

      if (!json.items?.some((r) => r.id === openId)) setOpenId(null);
    } catch (e) {
      console.error(e);
      setData((d) => ({ ...d, items: [] }));
      setOpenId(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPage(pageFromUrl, filtersFromUrl, qFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const didMountRef = useRef(false);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const query = q.trim();
    const timer = setTimeout(() => {
      if (!query) {
        setData((prev) => ({ ...prev, items: [], total: 0, page: 1 }));
        updateUrl(1, data.pageSize || 25, "", filters);
        return;
      }
      fetchPage(1, filters, query);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const sortedItems = useMemo(() => {
    const items = Array.isArray(data.items) ? [...data.items] : [];
    if (!sortBy) return items;

    const direction = sortDirection === "asc" ? 1 : -1;

    items.sort((a, b) => {
      if (sortBy === "company") {
        const av = String(a.ic_name || a.name || "").toLowerCase();
        const bv = String(b.ic_name || b.name || "").toLowerCase();
        return av.localeCompare(bv) * direction;
      }

      if (sortBy === "last_review_date") {
        const at = a.last_review_date ? new Date(a.last_review_date).getTime() : 0;
        const bt = b.last_review_date ? new Date(b.last_review_date).getTime() : 0;
        return (at - bt) * direction;
      }

      if (sortBy === "status") {
        const av = statusLabel(a.status);
        const bv = statusLabel(b.status);
        return av.localeCompare(bv) * direction;
      }

      return 0;
    });

    return items;
  }, [data.items, sortBy, sortDirection]);

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    setSortDirection("asc");
  };

  const sortIndicator = (column) => {
    if (sortBy !== column) return "";
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const currentUrl = `${location.pathname}?${searchParams.toString()}`;

  return (
    <ErrorBoundary componentName="OverviewPage">
      <div className="container db-container no-inner-scroll">
        <h1 className="page-title">Database Overview</h1>
        <p className="page-sub">Compact list with quick filters. Click a row to reveal full details; click the button to open the Company page.</p>

        <div className="card" style={{ marginTop: 8, marginBottom: 16 }}>
          <form
            className="toolbar"
            onSubmit={(e) => {
              e.preventDefault();
            }}>
            <input placeholder="Search name, IC, Coc no., UBO" value={q} onChange={(e) => setQ(e.target.value)} />

            <div className="spacer" />

            <button
              type="button"
              onClick={() => {
                setFilterOpen((v) => !v);
                queueMicrotask(() => filterAnchorRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }));
              }}
              disabled={loading}>
              Filter
            </button>
            <button type="submit" style={{ display: "none" }} />
          </form>

          <div ref={filterAnchorRef} />

          <InlineFilters
            visible={filterOpen}
            initial={filters}
            onApply={(sel) => {
              const next = normalizeFilter(sel);
              setFilters(next);
              fetchPage(1, next, q);
              setFilterOpen(false);
            }}
          />

          <div className="table-container db-table">
            <table className="table overview-compact">
              <thead>
                <tr>
                  <th style={{ width: 28 }} />
                  <th style={{ cursor: "pointer" }} onClick={() => toggleSort("company")}>
                    Company{sortIndicator("company")}
                  </th>
                  <th>CoC No</th>
                  <th>Residency</th>
                  <th>Risk</th>
                  <th style={{ cursor: "pointer" }} onClick={() => toggleSort("last_review_date")}>
                    Last Review{sortIndicator("last_review_date")}
                  </th>
                  <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                    Status{sortIndicator("status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {!data.items?.length && (
                  <tr>
                    <td colSpan={7} className="empty">
                      No rows.
                    </td>
                  </tr>
                )}

                {sortedItems.map((r) => (
                  <Fragment key={r.id}>
                    <tr className={`row-clickable statusrow-${r.status || ""}`} onClick={() => setOpenId(openId === r.id ? null : r.id)} title="Toggle details">
                      <td>{openId === r.id ? "▾" : "▸"}</td>
                      <td style={{ fontWeight: 700 }}>
                        {r.ic_name || r.name || "—"}
                        {r.ic_name && r.name && r.ic_name !== r.name ? <span className="muted"> &nbsp;• {r.name}</span> : null}
                      </td>
                      <td>{r.registration_no || "—"}</td>
                      <td>{r.residency_status || "—"}</td>
                      <td>{r.risk_level || "—"}</td>
                      <td>{r.last_review_date ? String(r.last_review_date).slice(0, 10) : "—"}</td>
                      <td>
                        <span className={`badge status-${r.status || "unknown"}`} title={r.status || "unknown"}>
                          {statusLabel(r.status)}
                        </span>
                      </td>
                    </tr>

                    {openId === r.id && (
                      <tr key={`${r.id}-detail`}>
                        <td colSpan={7}>
                          <div className="detail-grid">
                            <div>
                              <b>Country</b>
                              <div>{r.country || "—"}</div>
                            </div>
                            <div>
                              <b>Services</b>
                              <div>{r.services || "—"}</div>
                            </div>
                            <div>
                              <b>Business activity</b>
                              <div>{r.business_activity || "—"}</div>
                            </div>
                            <div>
                              <b>Group member</b>
                              <div>{yn(r.group_member)}</div>
                            </div>
                            <div>
                              <b>Group purpose</b>
                              <div>{r.group_purpose || "—"}</div>
                            </div>
                            <div>
                              <b>Relationship start</b>
                              <div>{fmtDate(r.relationship_started_at)}</div>
                            </div>

                            <div>
                              <b>Transaction profile</b>
                              <div>{triLabel(r.transaction_profile)}</div>
                            </div>
                            <div>
                              <b>Type of transactions</b>
                              <div>{fmtTx(r.transaction_types)}</div>
                            </div>
                            <div>
                              <b>Total assets</b>
                              <div>{formatMoney(r.total_assets, r.total_assets_currency)}</div>
                            </div>
                            <div>
                              <b>PEP</b>
                              <div>{yn(r.pep)}</div>
                            </div>
                            <div>
                              <b>Listed</b>
                              <div>{yn(r.listed)}</div>
                            </div>
                            <div>
                              <b>Bank account</b>
                              <div>{yn(r.bank_account)}</div>
                            </div>
                            <div>
                              <b>Foreign bank account</b>
                              <div>{yn(r.foreign_bank_account)}</div>
                            </div>
                            <div>
                              <b>FS last bookyear</b>
                              <div>{fmtYear(r.fs_last_bookyear)}</div>
                            </div>
                            <div>
                              <b>UBO origins</b>
                              <div>{fmtOrigins(r.ubo_origins)}</div>
                            </div>
                            <div>
                              <b>Shelf</b>
                              <div>{yn(r.shelf)}</div>
                            </div>
                            <div>
                              <b>Managed by</b>
                              <div>{managedByLabel(r.managed_by)}</div>
                            </div>
                          </div>

                          <div className="detail-actions">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                nav(`/company/${r.id}`, { state: { from: currentUrl } });
                              }}>
                              Open Company
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={data.page}
            totalPages={Math.ceil(data.total / (data.pageSize || 25))}
            totalItems={data.total}
            onPageChange={(page) => fetchPage(page, filters, q)}
            disabled={loading}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
}
